import { supabase } from '../lib/supabase';

/**
 * Audit Log Service
 * Tracks all sensitive actions for security and compliance
 */

/**
 * Log an audit event
 */
export async function logAuditEvent({
  actionType,
  actionDescription,
  entityType = null,
  entityId = null,
  metadata = null,
  severity = 'info'
}) {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    // Get IP address and user agent from browser
    const ipAddress = await fetch('https://api.ipify.org?format=json')?.then(res => res?.json())?.then(data => data?.ip)?.catch(() => 'unknown');
    
    const userAgent = navigator?.userAgent || 'unknown';
    
    const { data, error } = await supabase?.rpc('log_audit_event', {
      p_actor_user_id: user?.id || null,
      p_actor_email: user?.email || null,
      p_actor_ip_address: ipAddress,
      p_actor_user_agent: userAgent,
      p_action_type: actionType,
      p_action_description: actionDescription,
      p_entity_type: entityType,
      p_entity_id: entityId,
      p_metadata: metadata,
      p_severity: severity
    });
    
    if (error) throw error;
    
    return { success: true, auditId: data };
  } catch (error) {
    console.error('Log audit event error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get audit logs with filtering
 */
export async function getAuditLogs({
  userId = null,
  actionType = null,
  entityType = null,
  severity = null,
  startDate = null,
  endDate = null,
  limit = 100,
  offset = 0
}) {
  try {
    let query = supabase
      ?.from('audit_logs')
      ?.select('*', { count: 'exact' })
      ?.order('created_at', { ascending: false })
      ?.range(offset, offset + limit - 1);
    
    if (userId) {
      query = query?.eq('actor_user_id', userId);
    }
    
    if (actionType) {
      // Prefix match (e.g. "contract." or "payment.") uses ilike; exact values use eq
      if (actionType.endsWith('.')) {
        query = query?.ilike('action_type', `${actionType}%`);
      } else {
        query = query?.eq('action_type', actionType);
      }
    }
    
    if (entityType) {
      query = query?.eq('entity_type', entityType);
    }
    
    if (severity) {
      query = query?.eq('severity', severity);
    }
    
    if (startDate) {
      query = query?.gte('created_at', startDate);
    }
    
    if (endDate) {
      query = query?.lte('created_at', endDate);
    }
    
    const { data, error, count } = await query;
    
    if (error) throw error;
    
    return { success: true, data, total: count };
  } catch (error) {
    console.error('Get audit logs error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Export audit logs to CSV
 */
export async function exportAuditLogs(filters) {
  try {
    const result = await getAuditLogs({ ...filters, limit: 10000, offset: 0 });
    
    if (!result?.success) {
      throw new Error(result?.error);
    }
    
    const logs = result?.data;
    
    // Create CSV content
    const headers = [
      'Timestamp',
      'Actor Email',
      'Action Type',
      'Description',
      'Entity Type',
      'Entity ID',
      'IP Address',
      'Severity'
    ];
    
    const rows = logs?.map(log => [
      new Date(log?.created_at)?.toISOString(),
      log?.actor_email || 'N/A',
      log?.action_type,
      log?.action_description || '',
      log?.entity_type || 'N/A',
      log?.entity_id || 'N/A',
      log?.actor_ip_address || 'N/A',
      log?.severity
    ]);
    
    const csvContent = [
      headers?.join(','),
      ...rows?.map(row => row?.map(cell => `"${cell}"`)?.join(','))
    ]?.join('\n');
    
    // Create download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit_logs_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    window.URL?.revokeObjectURL(url);
    
    return { success: true };
  } catch (error) {
    console.error('Export audit logs error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get audit log statistics
 */
export async function getAuditLogStats() {
  try {
    const { data, error } = await supabase
      ?.from('audit_logs')
      ?.select('action_type, severity')
      ?.gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString());
    
    if (error) throw error;
    
    const stats = {
      totalEvents: data?.length,
      byActionType: {},
      bySeverity: {
        info: 0,
        warning: 0,
        critical: 0
      }
    };
    
    data?.forEach(log => {
      // Count by action type
      stats.byActionType[log?.action_type] = (stats?.byActionType?.[log?.action_type] || 0) + 1;
      
      // Count by severity
      stats.bySeverity[log?.severity] = (stats?.bySeverity?.[log?.severity] || 0) + 1;
    });
    
    return { success: true, data: stats };
  } catch (error) {
    console.error('Get audit log stats error:', error);
    return { success: false, error: error?.message };
  }
}